/**
 * Country-aware money formatting + small geo helpers used across checkout.
 *
 * Prices in DB are stored in INR by default (the historical base). When a
 * product is admin-tagged with a different currency, that currency is the
 * source of truth and we display it natively. For shoppers in markets where
 * we have a payment integration (IN / US / MV) we also convert the *base*
 * INR price for display, using env-driven FX rates kept in sync with the
 * server's INR_TO_USD_RATE / INR_TO_MVR_RATE.
 */
const FX_USD = parseFloat(process.env.REACT_APP_INR_TO_USD_RATE || '0.012');
const FX_MVR = parseFloat(process.env.REACT_APP_INR_TO_MVR_RATE || '0.18'); // ~ ₹1 = ރ.0.18

export const COUNTRIES = [
  { code: 'IN', name: 'India', currency: 'INR', dialCode: '+91', flag: '🇮🇳' },
  { code: 'US', name: 'United States', currency: 'USD', dialCode: '+1', flag: '🇺🇸' },
  { code: 'MV', name: 'Maldives', currency: 'MVR', dialCode: '+960', flag: '🇲🇻' },
];

// Currencies that an admin can tag a product with. Mirrors COUNTRIES + a few
// common reporting currencies, but the SHOP only converts between the
// supported-payment-region currencies at display time.
export const SUPPORTED_CURRENCIES = [
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'MVR', name: 'Maldivian Rufiyaa', symbol: 'Rf' },
];

export const countryByCode = (code) => COUNTRIES.find((c) => c.code === code) || COUNTRIES[0];
export const currencyForCountry = (code) => countryByCode(code).currency;

export const inrToUsd = (inr) => Math.round((inr || 0) * FX_USD * 100) / 100;
export const inrToMvr = (inr) => Math.round((inr || 0) * FX_MVR * 100) / 100;

// Convert an amount from any supported source currency into the display
// currency for the shopper's region. Returns { amount, currency }.
function convert(amount, fromCurrency, toCurrency) {
  const from = String(fromCurrency || 'INR').toUpperCase();
  const to = String(toCurrency || 'INR').toUpperCase();
  if (!amount || from === to) return { amount: Number(amount || 0), currency: to };
  // Normalize to INR base first.
  let inr = Number(amount);
  if (from === 'USD') inr = amount / (FX_USD || 0.012);
  else if (from === 'MVR') inr = amount / (FX_MVR || 0.18);
  // Then to target.
  if (to === 'USD') return { amount: Math.round(inr * FX_USD * 100) / 100, currency: 'USD' };
  if (to === 'MVR') return { amount: Math.round(inr * FX_MVR * 100) / 100, currency: 'MVR' };
  return { amount: Math.round(inr), currency: 'INR' };
}

/**
 * Format a price for display.
 *  - `formatMoney(amount)` — assumes INR amount, formats as INR.
 *  - `formatMoney(amount, 'US')` — assumes INR amount, converts + formats as USD.
 *  - `formatMoney(amount, 'MV', 'USD')` — amount is in USD, convert + format as MVR.
 *
 * Backwards-compatible with all existing two-arg callers (which assume INR base).
 */
export function formatMoney(amount, countryCode = 'IN', sourceCurrency = 'INR') {
  const targetCurrency = currencyForCountry(countryCode);
  const { amount: converted, currency } = convert(amount, sourceCurrency, targetCurrency);
  const localeMap = { INR: 'en-IN', USD: 'en-US', MVR: 'en-IN' };
  const maxFractionMap = { INR: 0, USD: 2, MVR: 2 };
  try {
    return new Intl.NumberFormat(localeMap[currency] || 'en', {
      style: 'currency',
      currency,
      maximumFractionDigits: maxFractionMap[currency] ?? 2,
    }).format(converted || 0);
  } catch {
    // Older browsers may reject some ISO codes; fall back to symbol + number.
    const symbol = SUPPORTED_CURRENCIES.find((c) => c.code === currency)?.symbol || currency;
    return `${symbol}${(converted || 0).toLocaleString()}`;
  }
}

/** Validate phone number client-side, country-aware. */
export function isValidPhone(value, countryCode = 'IN') {
  if (!value) return false;
  if (countryCode === 'US') return /^\+?1?\s*\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(value);
  // Maldives mobile numbers are 7 digits, optionally prefixed with +960.
  if (countryCode === 'MV') return /^(\+?960)?\s*\d{7}$/.test(value.replace(/\s|-/g, ''));
  return /^[6-9]\d{9}$/.test(value);
}

/** Validate postal code, country-aware. */
export function isValidPostal(value, countryCode = 'IN') {
  if (countryCode === 'US') return /^\d{5}(-\d{4})?$/.test(value || '');
  // Maldivian postcodes are 5 digits (e.g. 20026 for Malé).
  if (countryCode === 'MV') return /^\d{5}$/.test(value || '');
  return /^\d{6}$/.test(value || '');
}

/** US states (50 + DC) — used for state dropdown when country=US. */
export const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],
  ['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],
  ['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],
  ['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];
